import {
  addMonths,
  amountInSchema,
  compareText,
  compareYearMonth,
  MONTH_AMOUNTS,
  type MonthAmount,
  monthHasData,
  optionalHours,
  type Resource,
  type Timesheet,
  type TimesheetMonth,
  type TimesheetRow,
  toCents,
  type YearMonth,
} from '@pmai/contracts';

/**
 * Editing a Plan / Fact sheet on the card (FR-TS-003…016) — pure functions over the sheet as
 * the API returns it. Rows carry a client `key` so a row keeps its identity before and after the
 * server gives it an id.
 */
export interface EditRow extends TimesheetRow {
  key: string;
}
export interface EditSheet extends Omit<Timesheet, 'rows'> {
  rows: EditRow[];
}

let seq = 0;
export const newKey = (): string => `r${(seq += 1)}`;

export const toEditSheet = (sheet: Timesheet): EditSheet => ({
  ...sheet,
  rows: sheet.rows.map((r) => ({ ...r, key: newKey() })),
});

const ZERO = '0.00';
const blankMonth = (m: YearMonth): TimesheetMonth => ({
  year: m.year,
  month: m.month,
  locked: false,
  ...(Object.fromEntries(MONTH_AMOUNTS.map((k) => [k, ZERO])) as Record<MonthAmount, string>),
});

/** "📅 Add Month": the calendar month after the last one (FR-TS-003). */
export function addMonth(sheet: EditSheet): EditSheet {
  const next = addMonths(sheet.months[sheet.months.length - 1]!, 1);
  return {
    ...sheet,
    months: [...sheet.months, blankMonth(next)],
    rows: sheet.rows.map((r) => ({ ...r, hours: [...r.hours, 0] })),
  };
}

/** Why the last month cannot simply go (FR-TS-004): the only one, locked, or holding data. */
export function removeLastMonthCheck(sheet: EditSheet): 'only' | 'locked' | 'data' | 'ok' {
  const last = sheet.months.length - 1;
  if (last === 0) return 'only';
  if (sheet.months[last]!.locked) return 'locked';
  return monthHasData(sheet, last) ? 'data' : 'ok';
}

export function removeLastMonth(sheet: EditSheet): EditSheet {
  return {
    ...sheet,
    months: sheet.months.slice(0, -1),
    rows: sheet.rows.map((r) => ({ ...r, hours: r.hours.slice(0, -1) })),
  };
}

/** Months in front when the project now starts earlier; values stay in their months (FR-TS-016). */
export function extendToStart(sheet: EditSheet, start: YearMonth): EditSheet {
  const front: YearMonth[] = [];
  for (let m = start; compareYearMonth(m, sheet.months[0]!) < 0; m = addMonths(m, 1)) front.push(m);
  if (front.length === 0) return sheet;
  return {
    ...sheet,
    months: [...front.map(blankMonth), ...sheet.months],
    rows: sheet.rows.map((r) => ({ ...r, hours: [...front.map(() => 0), ...r.hours] })),
  };
}

export const toggleLock = (sheet: EditSheet, index: number): EditSheet => ({
  ...sheet,
  months: sheet.months.map((m, i) => (i === index ? { ...m, locked: !m.locked } : m)),
});

export const addRow = (sheet: EditSheet): EditSheet => ({
  ...sheet,
  rows: [
    ...sheet.rows,
    { key: newKey(), id: null, resourceId: null, name: '', rate: ZERO, hours: sheet.months.map(() => 0) },
  ],
});

export const removeRow = (sheet: EditSheet, key: string): EditSheet => ({
  ...sheet,
  rows: sheet.rows.filter((r) => r.key !== key),
});

const updateRow = (sheet: EditSheet, key: string, fn: (r: EditRow) => EditRow): EditSheet => ({
  ...sheet,
  rows: sheet.rows.map((r) => (r.key === key ? fn(r) : r)),
});

/**
 * Resource name (FR-TS-006): free text; a name that matches a catalogue resource links the row
 * to it and, while the rate is still 0, takes over the resource's rate.
 */
export function setRowName(
  sheet: EditSheet,
  key: string,
  name: string,
  resources: readonly Resource[],
): EditSheet {
  const match = resources.find((r) => compareText(r.name, name.trim()) === 0);
  return updateRow(sheet, key, (r) => ({
    ...r,
    name: name.trim(),
    resourceId: match?.id ?? null,
    rate: match?.hourlyRate && toCents(r.rate) === 0 ? match.hourlyRate : r.rate,
  }));
}

export const setRate = (sheet: EditSheet, key: string, rate: string): EditSheet =>
  updateRow(sheet, key, (r) => ({ ...r, rate }));

export const setHours = (sheet: EditSheet, key: string, index: number, hours: number): EditSheet =>
  updateRow(sheet, key, (r) => ({ ...r, hours: r.hours.map((h, i) => (i === index ? hours : h)) }));

export const setAmount = (sheet: EditSheet, field: MonthAmount, index: number, value: string): EditSheet => ({
  ...sheet,
  months: sheet.months.map((m, i) => (i === index ? { ...m, [field]: value } : m)),
});

export const setAdditionalPct = (sheet: EditSheet, pct: number): EditSheet => ({
  ...sheet,
  additionalPct: pct,
});

/** "📋→✅": Plan's months, rows and amounts become Fact's; rows are new there (FR-TS-015). */
export const copyPlanToFact = (plan: EditSheet, fact: EditSheet): EditSheet => ({
  ...fact,
  months: plan.months.map((m) => ({ ...m })),
  rows: plan.rows.map((r) => ({ ...r, id: null, key: newKey(), hours: [...r.hours] })),
  additionalPct: plan.additionalPct,
});

/** Typed text → hours (≥ 0, comma decimals, empty = 0); `null` when it is not a number. */
export function parseHours(text: string): number | null {
  const parsed = optionalHours.safeParse(text);
  return parsed.success ? (parsed.data ?? 0) : null;
}

/** Typed text → amount string ("1 234,5" → "1234.50", empty = 0); `null` when invalid. */
export function parseAmount(text: string): string | null {
  const parsed = amountInSchema.safeParse(text);
  return parsed.success ? parsed.data : null;
}

/** Typed text → percent 0–1000; `null` when invalid. */
export function parsePercent(text: string): number | null {
  const n = parseHours(text);
  return n !== null && n <= 1000 ? n : null;
}
